import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X } from 'lucide-react';
import { ease, duration } from '../../lib/motionConfig';

/**
 * InsightRevealToast — M6 "Wow Moment"
 *
 * A particle-materialize toast that coalesces ember sparks into a short
 * insight headline. Surfaces when the model produces a meaningful signal:
 *   • Tools were invoked (tool_calls > 0)
 *   • Memory was compressed (older turns auto-summarized)
 *   • Explicit trigger from the caller (kind: 'custom').
 *
 * Respects `prefers-reduced-motion` — falls back to a simple fade.
 *
 * Usage:
 *   <InsightRevealToast insight={insight} onDismiss={() => setInsight(null)} />
 *
 * where `insight` is:
 *   { id, headline, detail?, kind: 'tool' | 'memory' | 'custom', meta? }
 */

const KIND_LABEL = {
  tool: 'Tool Insight',
  memory: 'Memory Consolidated',
  custom: 'Insight',
};

// Deterministic pseudo-random so the particle positions render the same across
// React re-renders of the same insight but differ per insight id.
const seeded = (seed) => {
  let s = 0;
  for (let i = 0; i < seed.length; i++) s = (s * 31 + seed.charCodeAt(i)) >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
};

const PARTICLE_COUNT = 14;

export const InsightRevealToast = ({ insight, onDismiss, autoHideMs = 7000 }) => {
  const [visible, setVisible] = useState(!!insight);
  const prefersReduced = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    setVisible(!!insight);
    if (!insight || !autoHideMs) return;
    const t = setTimeout(() => setVisible(false), autoHideMs);
    return () => clearTimeout(t);
  }, [insight, autoHideMs]);

  const particles = useMemo(() => {
    if (!insight) return [];
    const rand = seeded(insight.id || insight.headline || 'x');
    return Array.from({ length: PARTICLE_COUNT }, () => ({
      x: (rand() - 0.5) * 220,
      y: (rand() - 0.5) * 120,
      size: 2 + rand() * 3,
      delay: rand() * 0.25,
      hue: rand() > 0.5 ? 'ember' : 'blue',
    }));
  }, [insight]);

  if (!insight) return null;
  const label = KIND_LABEL[insight.kind] || KIND_LABEL.custom;

  return (
    <AnimatePresence
      onExitComplete={() => {
        if (!visible && onDismiss) onDismiss();
      }}
    >
      {visible && (
        <motion.div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          data-testid="insight-reveal-toast"
          initial={{ opacity: 0, y: 16, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98, transition: { duration: duration.short, ease: ease.exit } }}
          transition={{ duration: duration.medium, ease: ease.entrance }}
          className="fixed z-[85] bottom-6 right-6 max-w-sm pointer-events-auto"
        >
          <div
            className="relative overflow-hidden rounded-2xl border backdrop-blur-xl"
            style={{
              background: 'color-mix(in srgb, var(--layer-2) 92%, transparent)',
              borderColor: 'var(--hairline-strong, var(--hairline))',
              boxShadow: '0 18px 48px -16px rgba(0,0,0,0.55), 0 0 0 1px rgba(242, 107, 31, 0.08)',
            }}
          >
            {/* Particle field — behind the headline */}
            {!prefersReduced && (
              <div aria-hidden="true" className="pointer-events-none absolute inset-0">
                {particles.map((p, i) => (
                  <motion.span
                    key={i}
                    initial={{ x: p.x, y: p.y, opacity: 0, scale: 0.6 }}
                    animate={{
                      x: 0,
                      y: 0,
                      opacity: [0, 1, 0],
                      scale: [0.6, 1, 0.4],
                    }}
                    transition={{
                      duration: 0.95,
                      ease: ease.entrance,
                      delay: p.delay,
                    }}
                    className="absolute left-1/2 top-1/2 rounded-full"
                    style={{
                      width: p.size,
                      height: p.size,
                      marginLeft: -p.size / 2,
                      marginTop: -p.size / 2,
                      background:
                        p.hue === 'ember'
                          ? 'radial-gradient(circle, rgba(242,107,31,0.95), rgba(242,107,31,0) 70%)'
                          : 'radial-gradient(circle, rgba(80,160,255,0.85), rgba(80,160,255,0) 70%)',
                      filter: 'blur(0.3px)',
                    }}
                  />
                ))}
              </div>
            )}

            <div className="relative flex items-start gap-3 p-4">
              <motion.div
                initial={{ scale: 0, rotate: -30 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ duration: duration.medium, ease: ease.entrance, delay: 0.15 }}
                className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(242,107,31,0.25), rgba(30,78,140,0.18))',
                  border: '1px solid rgba(242,107,31,0.35)',
                }}
              >
                <Sparkles size={16} style={{ color: 'var(--nexus-accent-ember, #F26B1F)' }} />
              </motion.div>

              <div className="flex-1 min-w-0">
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.22, duration: duration.short, ease: ease.entrance }}
                  className="ig-eyebrow text-[9.5px]"
                  style={{ color: 'var(--nexus-accent-ember, #F26B1F)' }}
                >
                  {label}
                </motion.div>
                <motion.p
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: duration.short, ease: ease.entrance }}
                  className="mt-1 text-sm font-medium leading-snug text-nexus-text-primary"
                >
                  {insight.headline}
                </motion.p>
                {insight.detail && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.42, duration: duration.short }}
                    className="mt-1 text-[11px] text-nexus-text-tertiary line-clamp-2"
                  >
                    {insight.detail}
                  </motion.p>
                )}
              </div>

              <button
                type="button"
                onClick={() => setVisible(false)}
                aria-label="Dismiss insight"
                data-testid="insight-reveal-dismiss"
                className="flex-shrink-0 p-1 -mr-1 -mt-1 rounded-md text-nexus-text-tertiary hover:text-nexus-text-primary hover:bg-nexus-bg-tertiary/50 transition-colors"
              >
                <X size={14} />
              </button>
            </div>

            {/* Bottom ember accent line */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: autoHideMs / 1000, ease: 'linear' }}
              style={{
                transformOrigin: 'left',
                background:
                  'linear-gradient(90deg, rgba(242,107,31,0.9), rgba(30,78,140,0.6))',
                height: 2,
              }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InsightRevealToast;
